import type { RouteObject } from "react-router-dom";
import AskHistoryPage from "../pages/admin/logs/ConversationHistoryTab";
import OperationLogsPage from "../pages/admin/logs/OperationLogsTab";
import SecurityRecordsPage from "../pages/admin/security-records/page";
import HealthMonitorPage from "../pages/admin/health-monitor/page";
import MVRSettingsPage from "../pages/admin/settings/mvr-settings/page";
import AuthGuard from "../components/auth/AuthGuard";
import AdminGuard from "../components/auth/AdminGuard";
import MaintenanceGuard from "../components/auth/MaintenanceGuard";
import { SYSTEM_LOG_ROLES } from "../utils/user";
import NotFound from "../pages/NotFound";
import LoginPage from "../pages/login/page";
import RecommendedQuestionsPage from "../pages/admin/recommended-questions/page";
import EditRecommendedQuestionPage from "../pages/admin/recommended-questions/edit/page";
import FeedbackPage from "../pages/admin/feedback/page";
import FeedbackDetailPage from "../pages/admin/feedback/detail/page";
import AnalyticsPage from "../pages/admin/analytics/page";
import PathSettingsPage from "../pages/admin/settings/path-settings/page";
import StudySourceSettingsPage from "../pages/admin/settings/study-source/page";
import UpdateLogsPage from "../pages/admin/settings/update-logs/page";
import UserPermissionsPage from "../pages/admin/users/page";
import OpsMate from "../pages/opsmate";
import OpsMateLayout from "../pages/opsmate/OpsMateLayout";
import AskSourcesPage from "../pages/ask-sources/page";
import PersonalSourcePage from "../pages/personal-source/page";
import DocumentsInOpsMatePage from "../pages/documents/opsmate-page";
import TermsOfUse from "../pages/terms-of-use";
import StudiesPage from "../pages/admin/studies/page";
import StudyDetailPage from "../pages/admin/studies/StudyDetailPage";
import DocumentCategoriesPage from "@/pages/admin/document-categories/page";
import DocumentTagsPage from "@/pages/admin/document-tags/page";
import RagSyncLogsPage from "@/pages/admin/logs/RagSyncLogsPage";
import AdminSyncConfigPage from "@/pages/admin/users/SyncConfigPage";
import SystemSettingsPage from "@/pages/admin/settings/page";

const routes: RouteObject[] = [
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    element: (
      <AuthGuard>
        <MaintenanceGuard>
          <OpsMateLayout />
        </MaintenanceGuard>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <OpsMate />,
      },
      {
        path: "opsmate",
        element: <OpsMate />,
      },
      {
        path: "general-source",
        element: <AskSourcesPage />,
      },
      {
        path: "personal-source",
        element: <PersonalSourcePage />,
      },
      {
        path: "study-source",
        element: <DocumentsInOpsMatePage />,
      },
    ],
  },
  {
    path: "/admin/recommended-questions",
    element: (
      <AdminGuard>
        <RecommendedQuestionsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/recommended-questions/:id",
    element: (
      <AdminGuard>
        <EditRecommendedQuestionPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/feedback",
    element: (
      <AdminGuard>
        <FeedbackPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/feedback/:id",
    element: (
      <AdminGuard>
        <FeedbackDetailPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/analytics",
    element: (
      <AdminGuard>
        <AnalyticsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/settings/general-source",
    element: (
      <AdminGuard>
        <PathSettingsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/settings/general-source/add",
    element: (
      <AdminGuard>
        <PathSettingsPage pageMode="add" />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/settings/general-source/:sourceId/edit",
    element: (
      <AdminGuard>
        <PathSettingsPage pageMode="edit" />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/settings/study-source",
    element: (
      <AdminGuard>
        <StudySourceSettingsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/settings/study-source/add",
    element: (
      <AdminGuard>
        <StudySourceSettingsPage pageMode="add" />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/settings/study-source/:sourceId/edit",
    element: (
      <AdminGuard>
        <StudySourceSettingsPage pageMode="edit" />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/settings/mvr-settings",
    element: (
      <AdminGuard>
        <MVRSettingsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/logs/update-logs",
    element: (
      <AdminGuard allowedRoles={SYSTEM_LOG_ROLES}>
        <UpdateLogsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/logs/operation-logs",
    element: (
      <AdminGuard allowedRoles={SYSTEM_LOG_ROLES}>
        <OperationLogsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/logs/rag-sync-logs",
    element: (
      <AdminGuard>
        <RagSyncLogsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/logs/ask-history",
    element: (
      <AdminGuard allowedRoles={SYSTEM_LOG_ROLES}>
        <AskHistoryPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/logs/update-logs",
    element: (
      <AdminGuard allowedRoles={SYSTEM_LOG_ROLES}>
        <UpdateLogsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/health-monitor",
    element: (
      <AdminGuard>
        <HealthMonitorPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/studies",
    element: (
      <AdminGuard>
        <StudiesPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/studies/:studyId",
    element: (
      <AdminGuard>
        <StudyDetailPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/document-categories",
    element: (
      <AdminGuard>
        <DocumentCategoriesPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/document-tags",
    element: (
      <AdminGuard>
        <DocumentTagsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/security-records",
    element: (
      <AdminGuard>
        <SecurityRecordsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/users",
    element: (
      <AdminGuard>
        <UserPermissionsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/users/add",
    element: (
      <AdminGuard>
        <UserPermissionsPage pageMode="add" />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/users/:userId/edit",
    element: (
      <AdminGuard>
        <UserPermissionsPage pageMode="edit" />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/users/dg-sync-settings",
    element: (
      <AdminGuard>
        <AdminSyncConfigPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/settings/system-settings",
    element: (
      <AdminGuard>
        <SystemSettingsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/terms-of-use",
    element: <TermsOfUse />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
